const API_URL = process.env.REACT_APP_API_URL || 'https://api.tcf-studio.dev';

const getHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const listCuratedImages = async () => {
  const response = await fetch(`${API_URL}/images/catalog`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch images');
  return data;
};

export const createCuratedImage = async (imageData) => {
  const response = await fetch(`${API_URL}/images/catalog`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(imageData)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create image');
  return data;
};

export const updateCuratedImage = async (imageId, imageData) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(imageData)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update image');
  return data;
};

// Write ONLY the playground config for an image. Isolated from
// updateCuratedImage so image-metadata edits and playground edits can never
// clobber each other (the mappings-disappearing bug).
export const setImagePlaygroundConfig = async (imageId, playgroundConfig) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}/playground`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ playgroundConfig })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update playground config');
  return data;
};

export const deleteCuratedImage = async (imageId) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete image');
  return data;
};

export const getImageGrants = async (imageId) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}/grants`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch image grants');
  return data;
};

export const addImageGrant = async (imageId, userId) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}/grants`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ userId })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to add image grant');
  return data;
};

export const removeImageGrant = async (imageId, userId) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}/grants/${userId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to remove image grant');
  return data;
};

export const updateImagePricing = async (imageId, pricingRates) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ pricingRates })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update pricing');
  return data;
};

export const convertToTcf = async (imageId) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}/convert`, {
    method: 'POST',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to start conversion');
  return data;
};

export const getConversionStatus = async (imageId) => {
  const response = await fetch(`${API_URL}/images/catalog/${imageId}/convert/status`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to get conversion status');
  return data;
};

export const listAccelerators = async () => {
  const response = await fetch(`${API_URL}/accelerators`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch accelerators');
  return data;
};

/**
 * Accelerators this account is allowed to launch, with the effective hourly
 * price for each (cheapest allowed provider, one GPU, one node).
 *
 * @param {string} tier - 'community' | 'datacenter' | 'both'
 * @param {boolean} useSpot - price against spot rather than on-demand
 */
export const listAvailableAccelerators = async (tier = 'both', useSpot = false, provider = '') => {
  const params = new URLSearchParams({ tier, use_spot: String(!!useSpot) });
  if (provider) params.set('provider', provider);
  const response = await fetch(`${API_URL}/accelerators/available?${params}`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch available accelerators');
  return data;
};

export default {
  listCuratedImages,
  createCuratedImage,
  updateCuratedImage,
  setImagePlaygroundConfig,
  deleteCuratedImage,
  getImageGrants,
  addImageGrant,
  removeImageGrant,
  updateImagePricing,
  convertToTcf,
  getConversionStatus,
  listAccelerators,
  listAvailableAccelerators,
  listPlaygroundPresets,
  savePlaygroundPreset,
  deletePlaygroundPreset
};

export const listPlaygroundPresets = async () => {
  const response = await fetch(`${API_URL}/playground/presets`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch presets');
  return data;
};

export const savePlaygroundPreset = async (name, config) => {
  const response = await fetch(`${API_URL}/playground/presets`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ name, config })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to save preset');
  return data;
};

export const deletePlaygroundPreset = async (presetId) => {
  const response = await fetch(`${API_URL}/playground/presets/${presetId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete preset');
  return data;
};
